import { Button, ButtonSize, ButtonType } from '@ardoq/button';
import { colors } from '@ardoq/design-tokens';
import { Icon, IconName, IconSize, MinimalIconProps } from '@ardoq/icons';
import { FlexBox, Stack } from '@ardoq/layout';
import {
  DropdownContainer,
  DropdownMaxHeight,
  DropdownMenu,
  DropdownOption as Popover2025DropdownOption,
  useDebouncedNestedPopover,
  useDropdownTriggerContainer,
} from '@ardoq/dropdown-2026';
import { Popover2025 } from '@ardoq/popovers';
import { useId, useRef } from 'react';
import {
  BooleanPopoverProps,
  ListPopoverProps,
  NumberPopoverProps,
  PopoverContent,
  RelativeDatePopoverProps,
  StringPopoverProps,
} from './filterPill/Popover';

export type TableFilterSelectorLabels = {
  buttonLabel: string;
  addFilterAriaLabel: string;
  submenuAriaLabel: (filterLabel: string) => string;
};

export const defaultTableFilterSelectorLabels: TableFilterSelectorLabels = {
  buttonLabel: 'Filter',
  addFilterAriaLabel: 'Add filter',
  submenuAriaLabel: (filterLabel: string) => `Submenu for ${filterLabel}`,
};

export type TableFilterSelectorViewProps = {
  popover: {
    isOpen: boolean;
    options: DropdownOptionViewProps[];
    setPopoverOpen: (isOpen: boolean) => void;
  };
  labels?: Partial<TableFilterSelectorLabels>;
};

export const TableFilterSelectorView = (
  props: TableFilterSelectorViewProps
) => {
  const popoverId = useId();
  const triggerRef = useDropdownTriggerContainer<HTMLButtonElement>({
    isOpen: props.popover.isOpen,
    openDropdown: () => props.popover.setPopoverOpen(true),
  });
  const labels = { ...defaultTableFilterSelectorLabels, ...props.labels };

  return (
    <>
      <Button
        onClick={() => {
          props.popover.setPopoverOpen(!props.popover.isOpen);
        }}
        buttonType={ButtonType.GHOST}
        buttonSize={ButtonSize.SMALL}
        ref={triggerRef}
        aria-label={labels.addFilterAriaLabel}
        aria-haspopup="listbox"
        aria-expanded={props.popover.isOpen}
        aria-controls={popoverId}
      >
        <Icon
          iconSize={IconSize.SMALL}
          color={colors.icon}
          iconName={IconName.FILTER_LIST}
        />
        {labels.buttonLabel}
      </Button>
      <Popover2025
        id={popoverId}
        role="listbox"
        aria-label={labels.addFilterAriaLabel}
        isOpen={props.popover.isOpen}
        anchorRef={triggerRef}
        onClose={() => props.popover.setPopoverOpen(false)}
        onClickOutside={() => props.popover.setPopoverOpen(false)}
        placement={{
          side: 'bottom',
          overflow: 'right',
        }}
      >
        <DropdownContainer dropdownMaxHeight={DropdownMaxHeight.LARGE}>
          <DropdownMenu>
            <Stack>
              {props.popover.options.map((option, index) => (
                <DropdownOptionView
                  key={index}
                  {...option}
                  submenuAriaLabel={labels.submenuAriaLabel}
                />
              ))}
            </Stack>
          </DropdownMenu>
        </DropdownContainer>
      </Popover2025>
    </>
  );
};

export type DropdownOptionViewProps = {
  popover: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    content:
      | BooleanPopoverProps
      | StringPopoverProps
      | NumberPopoverProps
      | RelativeDatePopoverProps
      | ListPopoverProps;
  };
  icon: MinimalIconProps | null;
  label: string;
};

const DropdownOptionView = (
  props: DropdownOptionViewProps & {
    submenuAriaLabel: (filterLabel: string) => string;
  }
) => {
  const { content } = props.popover;

  if (content.type === 'boolean') {
    return (
      <Popover2025DropdownOption
        onClick={content.onClick}
        aria-label={props.label}
      >
        <FlexBox align="center" gap="small">
          <Icon
            iconName={props.icon ? props.icon.name : undefined}
            iconSize={IconSize.SMALL}
            color={colors.icon}
          />
          {props.label}
        </FlexBox>
      </Popover2025DropdownOption>
    );
  }

  return (
    <NestedPopoverDropdownOption
      popover={{
        isOpen: props.popover.isOpen,
        setIsOpen: props.popover.setIsOpen,
        content,
      }}
      icon={props.icon}
      label={props.label}
      submenuAriaLabel={props.submenuAriaLabel}
    />
  );
};

const NestedPopoverDropdownOption = (props: {
  popover: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    content:
      | StringPopoverProps
      | NumberPopoverProps
      | RelativeDatePopoverProps
      | ListPopoverProps;
  };
  icon: MinimalIconProps | null;
  label: string;
  submenuAriaLabel: (filterLabel: string) => string;
}) => {
  const popoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const {
    handleTriggerMouseEnter,
    handleTriggerMouseLeave,
    handleChildMouseEnter,
    childPopoverRef,
  } = useDebouncedNestedPopover(props.popover.isOpen, props.popover.setIsOpen);

  return (
    <>
      <Popover2025DropdownOption
        onClick={() => props.popover.setIsOpen(true)}
        ref={triggerRef}
        aria-label={props.label}
        aria-haspopup="listbox"
        aria-expanded={props.popover.isOpen}
        aria-controls={popoverId}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
      >
        <FlexBox align="center" gap="small">
          <Icon
            iconName={props.icon ? props.icon.name : undefined}
            iconSize={IconSize.SMALL}
            color={colors.icon}
          />
          {props.label}
          <FlexBox marginLeft="auto">
            <Icon
              color={colors.icon}
              iconSize={IconSize.MEDIUM}
              iconName={IconName.CHEVRON_RIGHT}
            />
          </FlexBox>
        </FlexBox>
      </Popover2025DropdownOption>
      <Popover2025
        role="listbox"
        aria-label={props.submenuAriaLabel(props.label)}
        id={popoverId}
        isOpen={props.popover.isOpen}
        anchorRef={triggerRef}
        onClose={() => props.popover.setIsOpen(false)}
        onClickOutside={() => props.popover.setIsOpen(false)}
        focusContent
        placement={{
          side: 'right',
          overflow: 'bottom',
        }}
      >
        <div ref={childPopoverRef} onMouseEnter={handleChildMouseEnter}>
          {props.popover.isOpen && (
            <PopoverContent {...props.popover.content} />
          )}
        </div>
      </Popover2025>
    </>
  );
};
